import { Button } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'

import { AddEditDomainContext } from 'features/inventory/domain/contexts/AddEditDomainContext'
import { domainFromDTO } from 'features/inventory/mappers/domain'
import { Domain } from 'features/inventory/types/domain'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { useAbility } from 'providers/casl'
import { browserRelativeRedirect } from 'providers/router/utils'
import { mergeCloseAction } from 'providers/snackbar/mergeCloseAction'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { mapGraphQLErrors } from 'providers/tanstack-query/utils'
import { ROUTES } from 'routes'
import { AddSchema } from '../../schemas/addSchema'
import { CREATE_DOMAIN } from './mutation'
import { Data, UseCreateDomain, Variables } from './types'

export const useCreateDomain = (): UseCreateDomain => {
  const ability = useAbility()
  const { workspaceId } = useWorkspaceParam()
  const navigate = useNavigate()
  const { t } = useTranslation('features/domain')
  const { t: tCommon } = useTranslation('common')
  const { enqueueSnackbar } = useSnackbar()
  const { setLoading } = useContext(AddEditDomainContext)
  const [errors, setErrors] = useState<FieldErrors<AddSchema>>({})

  const { isPending, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: CREATE_DOMAIN(ability),
    onCompleted: async (data: Data) => {
      const {
        createDomain: { domain, errors },
      } = data

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('form.error.create'), { variant: 'error' })
      } else if (domain) {
        setErrors({})
        navigate(generatePath(ROUTES.INVENTORY.DOMAIN.LIST, { workspaceId }))
        enqueueSuccessSnackbar(domainFromDTO(domain))
      }
    },
  })

  useEffect(() => {
    setLoading(isPending)
  }, [isPending])

  const enqueueSuccessSnackbar = (domain: Domain): void => {
    const { id, name } = domain

    enqueueSnackbar(t('form.success.create', { domainName: name }), {
      action: snackbarId =>
        mergeCloseAction(
          <Button
            color='white'
            onClick={() => handleClickEdit(id)}
          >
            {tCommon('form.action.edit').toUpperCase()}
          </Button>,
          snackbarId,
        ),
      variant: 'success',
    })
  }

  const handleClickEdit = (id: Domain['id']): void =>
    browserRelativeRedirect(
      generatePath(ROUTES.INVENTORY.DOMAIN.EDIT, {
        id,
        tab: 'general',
        workspaceId,
      }),
    )

  const createDomain = (data: AddSchema): void => {
    mutateAsync({ input: data })
  }

  return { createDomain, errors }
}
